import React from 'react';
import {
  ReviewPolicyNodeData,
  ReviewSourceNodeData,
  ReviewVerdictNodeData,
  ReviewerCoreNodeData,
  ReviewerNode,
} from './graphTypes';
import { StatusGlyph } from './nodes/reviewerNodeStyles';
import {
  detailBlock,
  detailChip,
  detailHeading,
  detailMuted,
  detailRow,
} from './reviewerDetailStyles';

/** Where a withheld permission is actually granted — the one place it lives. */
export const PERMISSION_HINT = 'Permission set in Settings → Reviewer';

const SourceDetail: React.FC<{ data: ReviewSourceNodeData }> = ({ data }) => (
  <>
    <p className={detailRow}>{data.source.label}</p>
    {data.source.detail && <p className={detailMuted}>{data.source.detail}</p>}
    {(data.source.slots || []).length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {(data.source.slots || []).map((slot) => (
          <span key={slot} className={detailChip}>{`{{${slot}}}`}</span>
        ))}
      </div>
    ) : (
      <p className={detailMuted}>reaches the judge without a placeholder of its own</p>
    )}
  </>
);

const CoreDetail: React.FC<{ data: ReviewerCoreNodeData }> = ({ data }) => (
  <>
    <p className={detailRow}>{`the judge · ${data.checkpoint.role || 'no role'}`}</p>
    {data.checkpoint.tiers.map((tier) => (
      <p key={tier.id} className={detailMuted} data-testid={`reviewer-node-detail-tier-${tier.id}`}>
        {`${tier.id} · ${tier.model || 'no model'}${tier.inherited ? ' · inherited' : ''}`}
      </p>
    ))}
  </>
);

const PolicyDetail: React.FC<{ data: ReviewPolicyNodeData }> = ({ data }) => (
  <>
    <p className={detailRow}>{data.policy.label}</p>
    <p className={detailMuted}>{`on ${data.policy.verdict}`}</p>
    {data.policy.detail && <p className={detailMuted}>{data.policy.detail}</p>}
    {data.policy.permission_key && (
      <p className={detailMuted} data-testid="reviewer-node-detail-permission">
        {`${PERMISSION_HINT} · ${data.policy.permission_key}`}
      </p>
    )}
  </>
);

const VerdictDetail: React.FC<{ data: ReviewVerdictNodeData }> = ({ data }) => (
  <>
    <p className={detailRow}>what the judge can rule</p>
    <p className={detailMuted}>{data.verdicts.join(' · ')}</p>
  </>
);

/**
 * What ONE clicked node is, in the column (desktop) or under the tapped row
 * (phone) — the same block either way, so the two arrangements never explain
 * the same node differently.
 *
 * It says only what the node itself could not fit: a source's slots, a policy's
 * verdict and the permission that gates it, the judge's tier models.
 */
const ReviewerNodeDetail: React.FC<{ node: ReviewerNode }> = ({ node }) => {
  const data = node.data;
  const status =
    data.kind === 'review-source' || data.kind === 'review-policy' ? data.status : undefined;
  const label =
    data.kind === 'review-source'
      ? (data as ReviewSourceNodeData).source.label
      : data.kind === 'review-policy'
        ? (data as ReviewPolicyNodeData).policy.label
        : '';

  return (
    <div className={detailBlock} data-testid="reviewer-node-detail" data-node-id={node.id}>
      <div className="flex items-center gap-1.5">
        <span className={detailHeading}>selected</span>
        {status && <StatusGlyph status={status} label={label} className="ml-auto" />}
      </div>
      {data.kind === 'review-source' && <SourceDetail data={data} />}
      {data.kind === 'reviewer-core' && <CoreDetail data={data} />}
      {data.kind === 'review-policy' && <PolicyDetail data={data} />}
      {data.kind === 'review-verdict' && <VerdictDetail data={data} />}
    </div>
  );
};

export default ReviewerNodeDetail;
